import { Autocomplete, Paper, TextField, useTheme } from '@mui/material';
import { socialDatasetId } from "system/query/Query";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import APIRequest from 'system/APIRequest';
import { Box } from '@mui/system';

export interface QuickStartTerm {
  isEntity: boolean;
  id: string;
  label: string;
}

export interface QuickStartTermSelectorProps {
  term?: QuickStartTerm;
  onTermChange: (term?: QuickStartTerm) => void;
  onDeleteTerm: () => void;
}

export function QuickStartTermSelector(props: QuickStartTermSelectorProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const { term, onTermChange } = props;

  const [inputValue, setInputValue] = useState<string | null>(null);
  const [entityOptions, setEntityOptions] = useState<QuickStartTerm[]>([]);
  const [highlightedOption, setHighlightedOption] = useState<QuickStartTerm | null>(null);

  function searchEntities(searchString:string) {
    if (!searchString) {
      setEntityOptions([]);
      return;
    }
    if (searchString.length < 2) {
      setEntityOptions([{
        isEntity: false,
        id: searchString,
        label: searchString
      }]);
      return;
    }

    APIRequest(`suggest/${socialDatasetId}`, {
      field: 'entities',
      input: searchString,
      size: '50'
    }).then((response:any) => {
      const suggestions = response.suggestions.map((suggestion:any) => {
        return {
          isEntity: true,
          id: suggestion.id,
          label: suggestion.label
        } as QuickStartTerm;
      });
      suggestions.unshift({
        isEntity: false,
        id: searchString,
        label: searchString
      } as QuickStartTerm);
      setEntityOptions(suggestions);
    });
  }

  return (
    <Box sx={{ width: "200px", display: "inline-block", verticalAlign: "middle", mb: 2 }}>
    <Autocomplete
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        } else if (option.label) {
          return option.label
        } else {
          return i18n.t("null");
        }
      }}
      onKeyDown={(event) => {
        // if tab is pressed and there is a highlighted option, select it
        if (event.key === "Tab" && entityOptions.length) {
          if (highlightedOption) {
            onTermChange(highlightedOption);
            setInputValue("");
            setEntityOptions([]);
          } else if (inputValue?.length) {
            onTermChange({
              isEntity: false,
              id: inputValue,
              label: inputValue
            });
            setInputValue("");
            setEntityOptions([]);
          }
          event.preventDefault();
          event.stopPropagation();
        }
        // If the user presses backspace and there is no input, delete the last term
        if (event.key === "Backspace" && !inputValue) {
          props.onDeleteTerm();
        }
      }}
      onHighlightChange={(event, option) => {
        // Store the highlighted option for tab selection
        setHighlightedOption(option);
      }}
      size='medium'
      filterOptions={(x) => x}
      options={entityOptions}
      includeInputInList
      filterSelectedOptions
      inputValue={inputValue || ""}
      isOptionEqualToValue={(option, value) => {
        return option.id === value.id;
      }}
      value={
        term && (entityOptions.find(field => {
          return field.id === term.id;
        }) || (term.id && {id: term.id, label: term.label || term.id})) ||
        null
      }
      onChange={(event: React.SyntheticEvent<Element, Event>, newValue: any) => {
        if (newValue) {
          onTermChange({
            isEntity: newValue.isEntity,
            id: newValue.id,
            label: newValue.label
          });
          setInputValue("");
          setEntityOptions([]);
        } else {
          onTermChange(undefined);
        }
      }}
      onInputChange={(event: React.SyntheticEvent<Element, Event>, newInputValue: string) => {
        if (!event) {
          return;
        }

        // when deleting, check if we have an entity selected, if so clear it
        searchEntities(newInputValue);
        setInputValue(newInputValue);
      }}
      renderInput={(params) => {
        return (<TextField autoComplete="off" {...params} />);
      }}
      renderOption={({ key, ...props }, option: QuickStartTerm) => (
        <li key={key} {...props}>
          <Paper sx={{
            p:1, display: "block", width: "100%",
            border: "2px solid",
            borderColor: option.isEntity ? theme.colors.info.main : theme.colors.success.main
            }}>
            {!option.isEntity && <>{i18n.t("text")}: <strong>{option.label}</strong></>}
            {option.isEntity && option.label}
          </Paper>
        </li>
      )}
    />
    </Box>
  );
}
